import { useEffect, useRef } from "react";
import { useLocation } from "react-router";

interface IUseSearchPageCleanupArgs {
  inputValue: string;
  clearSearch: () => void;
}

export const useSearchPageCleanup = ({
  inputValue,
  clearSearch,
}: IUseSearchPageCleanupArgs): void => {
  const location = useLocation();

  const prevPathnameRef = useRef(location.pathname);
  const eventBaseValueRef = useRef<string | null>(null);
  const eventDirtyRef = useRef(false);

  const isOnEventPage = location.pathname.includes("/event/");

  useEffect(() => {
    if (!isOnEventPage) return;
    if (eventBaseValueRef.current !== null) return;

    eventBaseValueRef.current = inputValue;
    eventDirtyRef.current = false;
  }, [inputValue, isOnEventPage]);

  useEffect(() => {
    if (!isOnEventPage) return;
    if (eventBaseValueRef.current === null) return;
    if (eventDirtyRef.current) return;

    if (inputValue !== eventBaseValueRef.current) {
      eventDirtyRef.current = true;
    }
  }, [inputValue, isOnEventPage]);

  useEffect(() => {
    const prevPathname = prevPathnameRef.current;
    const prevIsEventPage = prevPathname.includes("/event/");

    const leavingEventPage = prevIsEventPage && !isOnEventPage;
    const enteringEventPage = !prevIsEventPage && isOnEventPage;

    if (leavingEventPage) {
      if (!eventDirtyRef.current) {
        clearSearch();
      }
      eventBaseValueRef.current = null;
      eventDirtyRef.current = false;
    } else if (enteringEventPage) {
      eventBaseValueRef.current = inputValue;
      eventDirtyRef.current = false;
    }

    prevPathnameRef.current = location.pathname;
  }, [clearSearch, inputValue, isOnEventPage, location.pathname]);
};
